import { AutoTranslate } from "../components/AutoTranslate";
import { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import { PublicHeader, PublicFooter, ShareBar } from "../components/public/PublicShell";
import { HELP_TOPICS, HELP_ARTICLES } from "../lib/helpArticles";
import { faqJsonLd, breadcrumbJsonLd } from "../lib/site";

// Public, indexable FAQ built from the in-app help articles (FAQPage schema).
export default function Faq() {
  const [openId, setOpenId] = useState(null);
  const groups = HELP_TOPICS.map((t) => ({ ...t, items: HELP_ARTICLES.filter((a) => a.topic === t.id) })).filter((g) => g.items.length);
  return (
    <AutoTranslate>
    <div className="min-h-screen bg-background text-foreground" data-testid="faq-page">
      <Seo title="FAQ — how HearthVault works" description="Answers to the most common questions about HearthVault: how filing works (and how AI is used), family roles, emergency access, estate planning tools, bank linking, security, privacy and plans." path="/faq" jsonLd={[faqJsonLd(HELP_ARTICLES.map((a) => ({ q: a.title, a: a.body }))), breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }])]} />
      <PublicHeader />
      <main className="max-w-4xl mx-auto px-6 py-14">
        <div className="text-[10px] uppercase tracking-widest font-mono text-primary flex items-center gap-1.5"><HelpCircle className="h-3.5 w-3.5" /> Frequently asked</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2 max-w-3xl" data-testid="faq-title">{HELP_ARTICLES.length} questions families ask before they trust us with the folder.</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl text-base sm:text-lg">Straight answers, grouped by topic. Members also get an AI help assistant inside the vault that answers from these same articles.</p>
        <ShareBar path="/faq" title="HearthVault FAQ" className="mt-4" />
        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Topics">{groups.map((g) => <a key={g.id} href={`#${g.id}`} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:border-primary hover:text-primary" data-testid={`faq-topic-${g.id}`}>{g.label}</a>)}</nav>
        <div className="mt-10 space-y-12">
          {groups.map((g) => (
            <section key={g.id} id={g.id} className="scroll-mt-24">
              <h2 className="font-serif text-2xl sm:text-3xl">{g.label}</h2>
              <dl className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card">
                {g.items.map((a) => {
                  const open = openId === a.id;
                  return (
                    <div key={a.id} id={a.id} className="scroll-mt-24" data-testid={`faq-item-${a.id}`}>
                      <dt>
                        <button type="button" onClick={() => setOpenId(open ? null : a.id)} aria-expanded={open} className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-muted/40 transition-colors" data-testid={`faq-toggle-${a.id}`}>
                          <span className="font-medium flex-1">{a.title}</span>
                          <ChevronDown className={`h-4 w-4 mt-1 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                      </dt>
                      <dd className={`px-5 text-sm text-muted-foreground leading-relaxed ${open ? "pb-5" : "sr-only"}`}>{a.body}</dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))}
        </div>
        <section className="mt-16 rounded-3xl bg-foreground text-background p-10 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1"><h2 className="font-serif text-3xl">Still wondering? Try it with 25 documents, free.</h2><p className="mt-2 opacity-80">No card, no trackers, one PIN. Your first bundle for the family in an afternoon.</p></div>
          <Button asChild size="lg" variant="secondary" className="rounded-full h-12 px-6 gap-2"><Link to="/welcome" data-testid="faq-cta-btn">Create your vault <ArrowRight className="h-4 w-4" /></Link></Button>
        </section>
      </main>
      <PublicFooter />
    </div>
    </AutoTranslate>
  );
}
